import Box from "@mui/material/Box";
import clsx from "clsx";
import React, { ButtonHTMLAttributes, HTMLProps } from "react";
import { Button, ButtonProps } from "./Button";
import { Surface } from "./Surface";
import Tooltip from "./Tooltip";
import { themeColors } from "@/utils/themeColors";

export type ToolbarWrapperProps = {
    shouldShowContent?: boolean;
    isVertical?: boolean;
} & HTMLProps<HTMLDivElement>;

const ToolbarWrapper = ({
    ref,
    shouldShowContent = true,
    children,
    isVertical = false,
    style,
    className,
    ...rest
}: ToolbarWrapperProps & {
    ref?: React.RefObject<HTMLDivElement | null>;
}) => {
    if (!shouldShowContent) {
        return null;
    }

    return (
        <Surface
            style={{
                color: "black",
                display: "inline-flex",
                lineHeight: 1,
                gap: "2px",
                width: "fit-content",
                maxWidth: "calc(100vw - 40px)",
                border: `1px solid ${themeColors.neutral1}`,
                ...(isVertical
                    ? { flexDirection: "column", padding: "8px" }
                    : { flexDirection: "row", padding: "4px", alignItems: "center" }),
                ...style,
            }}
            className={clsx(className)}
            {...rest}
            ref={ref}>
            {children}
        </Surface>
    );
};

export type ToolbarDividerProps = {
    horizontal?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const ToolbarDivider = ({
    ref,
    horizontal,
    style,
    ...rest
}: ToolbarDividerProps & {
    ref?: React.RefObject<HTMLDivElement>;
}) => {
    const dividerStyle = {
        backgroundColor: themeColors.neutral1,
        ...(horizontal
            ? {
                  width: "100%",
                  minWidth: "24px",
                  height: "1px",
                  marginTop: "4px",
                  marginBottom: "4px",
              }
            : {
                  height: "100%",
                  minHeight: "24px",
                  width: "1px",
                  margin: "auto 4px",
              }),
    };

    return <Box sx={{ flexShrink: 0, ...dividerStyle, ...style }} ref={ref} {...rest} />;
};

export type ToolbarButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    active?: boolean;
    tooltip?: string;
    tooltipShortcut?: string[];
    buttonSize?: ButtonProps["buttonSize"];
    variant?: ButtonProps["variant"];
};

const ToolbarButton = ({
    ref,
    children,
    buttonSize = "icon",
    variant = "ghost",
    style,
    tooltip,
    tooltipShortcut,
    ...rest
}: ToolbarButtonProps & {
    ref?: React.RefObject<HTMLButtonElement>;
}) => {
    const content = (
        <Button
            style={{
                gap: "4px",
                minWidth: "32px",
                paddingLeft: "8px",
                paddingRight: "8px",
                width: "fit-content",
                ...style,
            }}
            variant={variant}
            buttonSize={buttonSize}
            ref={ref}
            {...rest}
            data-testid="auto-id-ede99acb-b269-422e-9a8d-cbefe3cdf43f">
            {children}
        </Button>
    );

    if (tooltip) {
        return (
            <Tooltip title={tooltip} shortcut={tooltipShortcut}>
                {content}
            </Tooltip>
        );
    }

    return content;
};

export const Toolbar = {
    Wrapper: ToolbarWrapper,
    Divider: ToolbarDivider,
    Button: ToolbarButton,
};
